import { BlockBlobClient } from "@azure/storage-blob";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  mergeClasses,
  Select,
  Spinner,
  Textarea,
  Tooltip,
} from "@fluentui/react-components";
import {
  ArrowLeftRegular,
  ArrowRightRegular,
  CheckmarkRegular,
  DeleteRegular,
  DismissRegular,
  DocumentRegular,
  EditRegular,
  InfoRegular,
} from "@fluentui/react-icons";
import {
  addAttachment,
  deleteAttachment,
  deleteProject,
  getSignedUrl,
  updateAttachment,
} from "apps/decooda/decoodaapi";
import { IAttachment } from "apps/decooda/components/projects/projectCard.types";
import {
  createWorkspaceAnalytic,
  getWorkspaceAnalyticsForDiagnostic,
  updateWorkspaceAnalytic,
} from "apps/project-iq/api";
import { useMarkAnalyticsWorkspaceUpdated } from "apps/project-iq/hooks/useMarkAnalyticsWorkspaceUpdated";
import { WorkspaceFilePicker } from "apps/project-iq/components/WorkspaceFilePicker/WorkspaceFilePicker";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { DialogTabs } from "components/dialogTabs/DialogTabs";
import { FileDropzone } from "components/fileDropzone/FileDropzone";
import { IWorkspaceLibraryDocument } from "components/manageWorkspaces/workspaceLibrary/workspaceLibrary.types";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
  useSharedFileCardStyles,
} from "theme/sharedStyles";
import {
  ANALYTICS_VALID_FILE_EXTENSIONS,
  formatFileSize,
} from "apps/project-iq/utils/fileUtils";
import {
  fetchProjectIqWorkspaceDocumentAsFile,
  prepareHeaderBytes,
} from "utils/blobUtils";
import { v4 as uuidv4 } from "uuid";
import * as XLSX from "xlsx";
import { useStyles } from "./CreateAnalyticsDialog.styles";
import {
  CreateAnalyticsStep,
  DIALOG_LABELS,
  ICreateAnalyticsDialogProps,
  IPendingAttachment,
  STEP_LABELS,
} from "./CreateAnalyticsDialog.types";

const DELIMITER_OPTIONS = [
  { value: ",", label: "Comma (,)" },
  { value: ";", label: "Semicolon (;)" },
  { value: "\t", label: "Tab" },
  { value: "|", label: "Pipe (|)" },
];

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
const MAX_NAME_LENGTH = 100;
const MAX_OBJECTIVE_LENGTH = 1024;
const HTTP_STATUS_CONFLICT = 409;

type IApiError = Error & { status?: number };

const isConflictError = (error: unknown): error is IApiError =>
  error instanceof Error &&
  (error as IApiError).status === HTTP_STATUS_CONFLICT;
const VALID_MIME_TYPES = [
  "text/csv",
  "application/csv",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
const STEP_ORDER: CreateAnalyticsStep[] = ["basic-info", "data"];

const parseCSVColumns = (content: string, delimiter: string): string[] => {
  const lines = content.split("\n");
  if (lines.length === 0) return [];
  const firstLine = lines[0].replace(/\r$/, "");
  return firstLine
    .split(delimiter)
    .map((col) => col.trim().replace(/^"|"$/g, ""));
};

const parseXLSXColumns = async (
  file: File,
  sheetName?: string,
): Promise<string[]> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const targetSheet = workbook.Sheets[sheetName ?? workbook.SheetNames[0]];
  if (!targetSheet) return [];
  const rows = XLSX.utils.sheet_to_json<string[]>(targetSheet, { header: 1 });
  if (rows.length === 0) return [];
  return (rows[0] as (string | number | null)[])
    .map((col) => String(col ?? "").trim())
    .filter(Boolean);
};

const getXLSXSheetNames = async (file: File): Promise<string[]> => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  return workbook.SheetNames;
};

const getFileTypeBadgeLabel = (filename: string): "CSV" | "XLS" => {
  const ext = filename.substring(filename.lastIndexOf(".")).toLowerCase();
  return ext === ".csv" ? "CSV" : "XLS";
};

export const CreateAnalyticsDialog = ({
  isOpen,
  workspaceId,
  workspace,
  onDismiss,
  onSaved,
  mode = "create",
  project,
  diagnosticId,
  onCreated,
}: ICreateAnalyticsDialogProps): JSX.Element => {
  const footerStyles = useSharedDialogFooterStyles();
  const contentStyles = useSharedDialogContentStyles();
  const fileCardStyles = useSharedFileCardStyles();
  const styles = useStyles();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const markWorkspaceUpdated = useMarkAnalyticsWorkspaceUpdated(workspaceId);

  const [activeStep, setActiveStep] =
    useState<CreateAnalyticsStep>("basic-info");
  const [name, setName] = useState<string>("");
  const [objective, setObjective] = useState<string>("");
  const [clientName, setClientName] = useState<string>("");
  const [workdayCode, setWorkdayCode] = useState<string>("");
  const [nameError, setNameError] = useState<string>("");
  const [saveError, setSaveError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);

  const [attachments, setAttachments] = useState<IAttachment[]>([]);
  const [pendingAttachments, setPendingAttachments] = useState<
    IPendingAttachment[]
  >([]);
  const [attachmentError, setAttachmentError] = useState<string>("");
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState<boolean>(false);
  const [editingAttachmentId, setEditingAttachmentId] = useState<string | null>(
    null,
  );
  const [editingColumnValue, setEditingColumnValue] = useState<string>("");
  const [selectedWorkspaceFilename, setSelectedWorkspaceFilename] = useState<
    string | null
  >(null);
  const [isWorkspaceFileLoading, setIsWorkspaceFileLoading] =
    useState<boolean>(false);

  const createButtonRef = useRef<HTMLSpanElement>(null);

  const tabs = useMemo(
    () => STEP_ORDER.map((key) => ({ key, label: STEP_LABELS[key] })),
    [],
  );

  const isLastStep = activeStep === "data";

  const hasWorkspaceClientName = useMemo(
    () => !!workspace.clientName,
    [workspace.clientName],
  );
  const hasWorkspaceCode = useMemo(() => !!workspace.code, [workspace.code]);

  useEffect((): void => {
    if (!isOpen) return;

    setActiveStep("basic-info");
    setNameError("");
    setSaveError("");
    setAttachmentError("");
    setHasAttemptedSubmit(false);
    setIsLoading(false);
    setEditingAttachmentId(null);
    setEditingColumnValue("");
    setSelectedWorkspaceFilename(null);
    setIsWorkspaceFileLoading(false);

    if (mode === "edit" && project) {
      setName(project.name);
      setObjective(project.objective ?? "");
      setClientName(project.clientName ?? "");
      setWorkdayCode(project.workdayCode ?? "");
      setAttachments(project.attachments ?? []);
      setPendingAttachments([]);
      setCreatedProjectId(null);
    } else {
      setName("");
      setObjective("");
      setClientName(workspace.clientName ?? "");
      setWorkdayCode(workspace.code ?? "");
      setAttachments([]);
      setPendingAttachments([]);
      setCreatedProjectId(null);
    }
  }, [isOpen, mode, project, workspace.clientName, workspace.code]);

  useEffect((): void => {
    if (activeStep === "data") {
      createButtonRef.current
        ?.querySelector<HTMLButtonElement>("button")
        ?.focus();
    }
  }, [activeStep]);

  const handleNameChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setName(event.target.value.slice(0, MAX_NAME_LENGTH));
    if (nameError) setNameError("");
  };

  const handleObjectiveChange = (
    _event: React.ChangeEvent<HTMLTextAreaElement>,
    data: { value: string },
  ): void => {
    setObjective(data.value.slice(0, MAX_OBJECTIVE_LENGTH));
  };

  const handleNext = async (): Promise<void> => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError(DIALOG_LABELS.NAME_REQUIRED);
      return;
    }
    setName(trimmedName);
    setSaveError("");
    setIsLoading(true);

    try {
      if (mode === "create") {
        if (createdProjectId) {
          await updateWorkspaceAnalytic({
            workspaceId,
            projectId: createdProjectId,
            name: trimmedName,
            objective: objective.trim(),
            clientName: clientName.trim(),
            workdayCode: workdayCode.trim(),
          });
        } else {
          const result = await createWorkspaceAnalytic({
            workspaceId,
            name: trimmedName,
            objective: objective.trim(),
            clientName: clientName.trim(),
            workdayCode: workdayCode.trim(),
            diagnosticId,
          });
          const createdId = result?.id as string | undefined;
          if (!createdId)
            throw new Error("Project creation failed — no id returned");
          setCreatedProjectId(createdId);
        }
      } else {
        if (!project) return;
        const hasChanges =
          trimmedName !== project.name ||
          objective.trim() !== (project.objective ?? "") ||
          clientName.trim() !== (project.clientName ?? "") ||
          workdayCode.trim() !== (project.workdayCode ?? "");
        if (hasChanges) {
          await updateWorkspaceAnalytic({
            workspaceId,
            projectId: project.id,
            name: trimmedName,
            objective: objective.trim(),
            clientName: clientName.trim(),
            workdayCode: workdayCode.trim(),
          });
        }
      }

      setActiveStep("data");
    } catch (error) {
      if (
        mode === "create" &&
        !createdProjectId &&
        diagnosticId &&
        isConflictError(error)
      ) {
        try {
          const linkedProjects = await getWorkspaceAnalyticsForDiagnostic({
            workspaceId,
            diagnosticId,
          });
          const existingProject = linkedProjects?.[0];
          if (existingProject?.id) {
            setCreatedProjectId(existingProject.id);
            setAttachments(existingProject.attachments ?? []);
            setActiveStep("data");
            return;
          }
        } catch (recoveryError) {
          const reason =
            recoveryError instanceof Error ? recoveryError.message : undefined;
          setSaveError(reason ? `${error.message} (${reason})` : error.message);
          return;
        }
        setSaveError(error.message);
        return;
      }

      setSaveError(
        mode === "edit"
          ? DIALOG_LABELS.ERROR_UPDATE
          : DIALOG_LABELS.ERROR_CREATE,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleBack = (): void => {
    setActiveStep("basic-info");
  };

  const handleTabClick = (tab: string): void => {
    const target = tab as CreateAnalyticsStep;
    const targetIndex = STEP_ORDER.indexOf(target);
    const currentIndex = STEP_ORDER.indexOf(activeStep);

    if (targetIndex > currentIndex) {
      // Forward navigation — same as clicking Next
      void handleNext();
      return;
    }
    setActiveStep(target);
  };

  const handleSubmit = async (): Promise<void> => {
    setHasAttemptedSubmit(true);

    const totalCount = attachments.length + pendingAttachments.length;
    if (totalCount === 0) {
      setAttachmentError("At least one data file is required.");
      return;
    }

    const hasIncomplete = pendingAttachments.some((a) => !a.contentColumn);
    if (hasIncomplete) {
      setAttachmentError("Please select a content column for all data files.");
      return;
    }

    if (pendingAttachments.length > 0) {
      setAttachmentError(
        "Please wait for all uploads to complete, or remove pending files.",
      );
      return;
    }

    if (mode === "create" && createdProjectId) {
      await markWorkspaceUpdated(createdProjectId);
      if (onCreated) {
        onCreated(createdProjectId);
      } else {
        navigate(`/workspace/${workspaceId}/ai-analytics/${createdProjectId}`);
      }
      onDismiss();
    } else if (mode === "edit") {
      if (project) await markWorkspaceUpdated(project.id);
      onSaved?.();
      onDismiss();
    }
  };

  const handleDismiss = async (): Promise<void> => {
    if (mode === "create" && createdProjectId) {
      try {
        await deleteProject({ projectId: createdProjectId, isDraft: true });
      } catch {
        void 0;
      }
    }
    onDismiss();
  };

  const resolveProjectId = (): string | undefined =>
    mode === "edit" ? project?.id : (createdProjectId ?? undefined);

  const handleDropzoneFiles = async (files: File[]): Promise<void> => {
    if (!resolveProjectId()) {
      setAttachmentError("Project not ready. Please try again.");
      return;
    }
    await processFiles(files);
  };

  const handleWorkspaceFileSelected = async (
    doc: IWorkspaceLibraryDocument | null,
  ): Promise<void> => {
    if (!doc) return;
    if (!resolveProjectId()) {
      setAttachmentError("Project not ready. Please try again.");
      return;
    }

    setSelectedWorkspaceFilename(doc.filename);
    setIsWorkspaceFileLoading(true);
    setAttachmentError("");

    try {
      const file = await fetchProjectIqWorkspaceDocumentAsFile(
        workspaceId,
        doc,
      );
      await processFiles([file]);
    } catch (error) {
      const reason = error instanceof Error ? error.message : undefined;
      setAttachmentError(
        `Failed to load "${doc.originalFilename}" from the workspace library.` +
          (reason ? ` ${reason}` : " Please try again."),
      );
    } finally {
      setIsWorkspaceFileLoading(false);
    }
  };

  const processFiles = async (files: File[]): Promise<void> => {
    const newPending: IPendingAttachment[] = [];
    const skipped: string[] = [];

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        skipped.push(`${file.name} (exceeds 50 MB limit)`);
        continue;
      }
      if (!VALID_MIME_TYPES.includes(file.type)) {
        skipped.push(`${file.name} (invalid file type)`);
        continue;
      }
      const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
      if (!ANALYTICS_VALID_FILE_EXTENSIONS.includes(ext)) {
        skipped.push(`${file.name} (invalid extension)`);
        continue;
      }

      try {
        const isXlsx = ext === ".xlsx";
        let columns: string[];
        let sheetNames: string[] = [];
        let selectedSheet = "";
        const defaultDelimiter = ",";

        if (isXlsx) {
          sheetNames = await getXLSXSheetNames(file);
          selectedSheet = sheetNames[0] ?? "";
          columns = await parseXLSXColumns(file, selectedSheet || undefined);
        } else {
          const content = await file.text();
          if (content.length === 0) {
            skipped.push(`${file.name} (empty file)`);
            continue;
          }
          columns = parseCSVColumns(content, defaultDelimiter);
        }

        if (columns.length === 0) {
          skipped.push(`${file.name} (could not parse columns)`);
          continue;
        }

        newPending.push({
          id: uuidv4(),
          file,
          delimiter: defaultDelimiter,
          columns,
          contentColumn: "",
          isUploading: false,
          sheetNames,
          selectedSheet,
        });
      } catch {
        skipped.push(`${file.name} (read error)`);
      }
    }

    if (skipped.length > 0) {
      setAttachmentError(
        `Skipped ${skipped.length} file(s): ${skipped.join(", ")}`,
      );
    } else {
      setAttachmentError("");
    }

    if (newPending.length > 0) {
      setPendingAttachments((prev) => [...prev, ...newPending]);
      setHasAttemptedSubmit(false);
    }
  };

  const handleDelimiterChange = async (
    attachmentId: string,
    delimiter: string,
  ): Promise<void> => {
    const attachment = pendingAttachments.find((a) => a.id === attachmentId);
    if (!attachment) return;

    const ext = attachment.file.name
      .substring(attachment.file.name.lastIndexOf("."))
      .toLowerCase();
    const columns =
      ext === ".xlsx"
        ? await parseXLSXColumns(
            attachment.file,
            attachment.selectedSheet || undefined,
          )
        : parseCSVColumns(await attachment.file.text(), delimiter);

    setPendingAttachments((prev) =>
      prev.map((a) =>
        a.id === attachmentId
          ? { ...a, delimiter, columns, contentColumn: columns[0] ?? "" }
          : a,
      ),
    );
  };

  const handleSheetChange = async (
    attachmentId: string,
    sheet: string,
  ): Promise<void> => {
    const attachment = pendingAttachments.find((a) => a.id === attachmentId);
    if (!attachment) return;

    const columns = await parseXLSXColumns(attachment.file, sheet || undefined);
    setPendingAttachments((prev) =>
      prev.map((a) =>
        a.id === attachmentId
          ? {
              ...a,
              selectedSheet: sheet,
              columns,
              contentColumn: columns[0] ?? "",
            }
          : a,
      ),
    );
  };

  const handleContentColumnChange = async (
    attachmentId: string,
    column: string,
  ): Promise<void> => {
    if (!column) {
      setPendingAttachments((prev) =>
        prev.map((a) =>
          a.id === attachmentId ? { ...a, contentColumn: column } : a,
        ),
      );
      return;
    }

    const attachment = pendingAttachments.find((a) => a.id === attachmentId);
    if (!attachment || attachment.isUploading) return;

    await uploadAttachment(attachmentId, column, attachment);
  };

  const uploadAttachment = async (
    attachmentId: string,
    contentColumn: string,
    attachment: IPendingAttachment,
  ): Promise<void> => {
    const projectId = resolveProjectId();
    if (!projectId) return;

    setPendingAttachments((prev) =>
      prev.map((a) =>
        a.id === attachmentId ? { ...a, contentColumn, isUploading: true } : a,
      ),
    );

    try {
      const fileData = await prepareHeaderBytes(attachment.file);
      const { sas, containerName, blobName } = await getSignedUrl({
        filename: attachment.file.name,
        projectId,
        fileData,
      });

      const sasUrl = new URL(sas as string);
      const blobUrl = `${sasUrl.origin}/${containerName as string}/${blobName as string}${sasUrl.search}`;
      const blobClient = new BlockBlobClient(blobUrl);
      await blobClient.uploadData(attachment.file, {
        blobHTTPHeaders: { blobContentType: attachment.file.type },
      });

      const result = (await addAttachment({
        projectId,
        filename: attachment.file.name,
        fileUrl: blobName,
        delimiter: attachment.delimiter,
        contentColumn,
        columns: attachment.columns,
        sheetName: attachment.selectedSheet || undefined,
      })) as { attachment: IAttachment };

      setAttachments((prev) => [...prev, result.attachment]);
      setPendingAttachments((prev) =>
        prev.filter((a) => a.id !== attachmentId),
      );
      setAttachmentError("");
    } catch {
      setAttachmentError("Failed to upload file. Please try again.");
      setPendingAttachments((prev) =>
        prev.map((a) =>
          a.id === attachmentId ? { ...a, isUploading: false } : a,
        ),
      );
    }
  };

  const handleRemovePending = (attachmentId: string): void => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
  };

  const handleDeleteAttachment = async (
    attachmentId: string,
  ): Promise<void> => {
    const projectId = resolveProjectId();
    if (!projectId) return;

    try {
      await deleteAttachment({ projectId, attachmentId });
      setAttachments((prev) => prev.filter((a) => a.id !== attachmentId));
    } catch {
      setAttachmentError("Failed to delete file. Please try again.");
    }
  };

  const handleStartEditAttachment = (
    attachmentId: string,
    currentColumn: string,
    columns: string[],
  ): void => {
    setEditingAttachmentId(attachmentId);
    setEditingColumnValue(currentColumn || columns[0] || "");
    setAttachmentError("");
  };

  const handleCancelEditAttachment = (): void => {
    setEditingAttachmentId(null);
    setEditingColumnValue("");
  };

  const handleSaveEditAttachment = async (
    attachmentId: string,
  ): Promise<void> => {
    const projectId = resolveProjectId();
    if (!projectId) return;

    const attachment = attachments.find((a) => a.id === attachmentId);
    if (!attachment || !editingColumnValue) return;

    try {
      await updateAttachment({
        projectId,
        attachmentId,
        delimiter: attachment.delimiter,
        contentColumn: editingColumnValue,
        sheetName: attachment.sheetName || undefined,
        columns: attachment.columns,
      });
      setAttachments((prev) =>
        prev.map((a) =>
          a.id === attachmentId
            ? { ...a, contentColumn: editingColumnValue }
            : a,
        ),
      );
      setEditingAttachmentId(null);
      setEditingColumnValue("");
      void queryClient.invalidateQueries(["workspace-analytics", workspaceId]);
      if (projectId) {
        void queryClient.invalidateQueries(["analytics-project", projectId]);
      }
    } catch {
      setAttachmentError("Failed to update attachment. Please try again.");
    }
  };

  const isNameEmpty = !name.trim();
  const isNextDisabled = activeStep === "basic-info" && isNameEmpty;
  const hasNoAttachments =
    attachments.length === 0 && pendingAttachments.length === 0;
  const isSubmitDisabled = isLoading || hasNoAttachments;

  return (
    <Dialog
      open={isOpen}
      modalType="modal"
      onOpenChange={(_, data) => {
        if (!data.open && !isLoading) void handleDismiss();
      }}
    >
      <DialogSurface
        className={mergeClasses(
          contentStyles.dialogSurface,
          styles.wideDialogSurface,
        )}
      >
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                aria-label={DIALOG_LABELS.CLOSE}
                onClick={() => void handleDismiss()}
                disabled={isLoading}
              />
            }
          >
            {mode === "edit" ? DIALOG_LABELS.TITLE_EDIT : DIALOG_LABELS.TITLE}
          </DialogTitle>

          <DialogContent
            className={mergeClasses(
              contentStyles.tabbedDialogContent,
              styles.dialogContent,
            )}
          >
            {isLoading && (
              <div
                className={contentStyles.formLoadingOverlay}
                aria-live="assertive"
                aria-atomic="true"
              >
                <Spinner
                  label={`${mode === "edit" ? DIALOG_LABELS.SAVING_EDIT : DIALOG_LABELS.SAVING}...`}
                />
              </div>
            )}

            <DialogTabs
              tabs={tabs}
              activeTab={activeStep}
              onTabClick={handleTabClick}
            />

            <div
              className={mergeClasses(
                contentStyles.formContainer,
                contentStyles.scrollableContent,
              )}
            >
              {activeStep === "basic-info" && (
                <div
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  id="tabpanel-basic-info"
                  aria-labelledby="tab-basic-info"
                >
                  <div className={styles.formContainer}>
                    <Field
                      label={DIALOG_LABELS.NAME_LABEL}
                      required
                      validationState={nameError ? "error" : "none"}
                      validationMessage={nameError || undefined}
                      hint={`${name.length}/${MAX_NAME_LENGTH}`}
                    >
                      <Input
                        value={name}
                        onChange={handleNameChange}
                        placeholder={DIALOG_LABELS.NAME_PLACEHOLDER}
                        disabled={isLoading}
                        maxLength={MAX_NAME_LENGTH}
                      />
                    </Field>

                    <Field label={DIALOG_LABELS.OBJECTIVE_LABEL}>
                      <Textarea
                        value={objective}
                        onChange={handleObjectiveChange}
                        resize="vertical"
                        rows={3}
                        disabled={isLoading}
                        placeholder={DIALOG_LABELS.OBJECTIVE_PLACEHOLDER}
                        maxLength={MAX_OBJECTIVE_LENGTH}
                      />
                    </Field>

                    <div className={styles.inheritedSection}>
                      <div className={styles.inheritedHeader}>
                        <p className={styles.inheritedLabel}>
                          {DIALOG_LABELS.WORKSPACE_SECTION_LABEL}
                        </p>
                        <p className={styles.inheritedHint}>
                          Pre-filled from <strong>{workspace.name}</strong>.
                          Edit if this analysis belongs to a different client or
                          billing code.
                        </p>
                      </div>

                      <div className={styles.inheritedGrid}>
                        <Field label={DIALOG_LABELS.WORKSPACE_LABEL}>
                          <Input
                            value={workspace.name}
                            readOnly
                            disabled
                            aria-label={DIALOG_LABELS.WORKSPACE_LABEL}
                          />
                        </Field>

                        <Field
                          label={{
                            children: (
                              <span className={styles.fieldLabelRow}>
                                {DIALOG_LABELS.CLIENT_NAME_LABEL}
                                {hasWorkspaceClientName && (
                                  <span className={styles.prefillBadge}>
                                    {DIALOG_LABELS.PREFILLED}
                                  </span>
                                )}
                              </span>
                            ),
                          }}
                        >
                          <Input
                            value={clientName}
                            onChange={(_, data) => setClientName(data.value)}
                            placeholder={DIALOG_LABELS.CLIENT_NAME_PLACEHOLDER}
                            disabled={isLoading}
                            aria-label={DIALOG_LABELS.CLIENT_NAME_LABEL}
                          />
                        </Field>

                        <Field
                          label={{
                            children: (
                              <span className={styles.fieldLabelRow}>
                                {DIALOG_LABELS.WORKDAY_CODE_LABEL}
                                {hasWorkspaceCode && (
                                  <span className={styles.prefillBadge}>
                                    {DIALOG_LABELS.PREFILLED}
                                  </span>
                                )}
                              </span>
                            ),
                          }}
                        >
                          <Input
                            value={workdayCode}
                            onChange={(_, data) => setWorkdayCode(data.value)}
                            placeholder={DIALOG_LABELS.WORKDAY_CODE_PLACEHOLDER}
                            disabled={isLoading}
                            aria-label={DIALOG_LABELS.WORKDAY_CODE_LABEL}
                          />
                        </Field>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeStep === "data" && (
                <div
                  className={contentStyles.tabContent}
                  role="tabpanel"
                  id="tabpanel-data"
                  aria-labelledby="tab-data"
                >
                  <div className={styles.infoBanner} role="note">
                    <InfoRegular
                      className={styles.infoBannerIcon}
                      aria-hidden="true"
                    />
                    <span>{DIALOG_LABELS.DATA_INFO_BANNER}</span>
                  </div>

                  <Field
                    validationState={attachmentError ? "error" : "none"}
                    validationMessage={attachmentError || undefined}
                  >
                    {diagnosticId ? (
                      isWorkspaceFileLoading ? (
                        <div className={styles.workspaceFileLoading}>
                          <Spinner
                            size="tiny"
                            label="Loading file from workspace..."
                          />
                        </div>
                      ) : (
                        <WorkspaceFilePicker
                          workspaceId={workspaceId}
                          selectedFilename={selectedWorkspaceFilename}
                          onSelectionChange={(doc) =>
                            void handleWorkspaceFileSelected(doc)
                          }
                        />
                      )
                    ) : (
                      <FileDropzone
                        onFilesSelected={(files) =>
                          void handleDropzoneFiles(files)
                        }
                        acceptedExtensions={ANALYTICS_VALID_FILE_EXTENSIONS}
                        multiple
                        onValidationError={(msg) => setAttachmentError(msg)}
                      />
                    )}
                  </Field>

                  {(attachments.length > 0 ||
                    pendingAttachments.length > 0) && (
                    <div className={styles.attachmentList}>
                      {attachments.map((attachment) => {
                        const badge = getFileTypeBadgeLabel(
                          attachment.filename,
                        );
                        const isEditing = editingAttachmentId === attachment.id;

                        return (
                          <div
                            key={attachment.id}
                            className={styles.confirmedCard}
                          >
                            <span
                              className={mergeClasses(
                                styles.fileTypeBadge,
                                badge === "CSV"
                                  ? styles.fileTypeBadgeCsv
                                  : styles.fileTypeBadgeXls,
                              )}
                              aria-label={`File type: ${badge}`}
                            >
                              {badge}
                            </span>
                            <div className={styles.confirmedCardInfo}>
                              <span className={styles.confirmedFileName}>
                                {attachment.filename}
                              </span>
                              {isEditing ? (
                                <div className={styles.fieldsRow}>
                                  <Field
                                    label="Content Column"
                                    size="small"
                                    required
                                  >
                                    <Select
                                      value={editingColumnValue}
                                      onChange={(_, data) =>
                                        setEditingColumnValue(data.value)
                                      }
                                      size="small"
                                      className={styles.fieldSelectWide}
                                      aria-label="Content column"
                                    >
                                      {attachment.columns.map((col) => (
                                        <option
                                          key={col}
                                          value={col}
                                          title={col}
                                        >
                                          {col.length > 25
                                            ? `${col.substring(0, 25)}...`
                                            : col}
                                        </option>
                                      ))}
                                    </Select>
                                  </Field>
                                </div>
                              ) : (
                                attachment.contentColumn && (
                                  <span className={styles.confirmedFileDetails}>
                                    {attachment.sheetName
                                      ? `Sheet: ${attachment.sheetName} · `
                                      : ""}
                                    Content Column: {attachment.contentColumn}
                                  </span>
                                )
                              )}
                            </div>
                            {isEditing ? (
                              <>
                                <Tooltip content="Save" relationship="label">
                                  <Button
                                    appearance="subtle"
                                    icon={
                                      <CheckmarkRegular aria-hidden="true" />
                                    }
                                    aria-label="Save column selection"
                                    className={fileCardStyles.fileDeleteButton}
                                    onClick={() =>
                                      void handleSaveEditAttachment(
                                        attachment.id,
                                      )
                                    }
                                    disabled={!editingColumnValue}
                                  />
                                </Tooltip>
                                <Tooltip content="Cancel" relationship="label">
                                  <Button
                                    appearance="subtle"
                                    icon={<DismissRegular aria-hidden="true" />}
                                    aria-label="Cancel editing"
                                    className={fileCardStyles.fileDeleteButton}
                                    onClick={handleCancelEditAttachment}
                                  />
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                {attachment.columns.length > 0 && (
                                  <Tooltip
                                    content="Edit column"
                                    relationship="label"
                                  >
                                    <Button
                                      appearance="subtle"
                                      icon={<EditRegular aria-hidden="true" />}
                                      aria-label={`Edit column for ${attachment.filename}`}
                                      className={
                                        fileCardStyles.fileDeleteButton
                                      }
                                      onClick={() =>
                                        handleStartEditAttachment(
                                          attachment.id,
                                          attachment.contentColumn ?? "",
                                          attachment.columns,
                                        )
                                      }
                                    />
                                  </Tooltip>
                                )}
                                <Tooltip
                                  content={`Delete ${attachment.filename}`}
                                  relationship="label"
                                >
                                  <Button
                                    appearance="subtle"
                                    icon={<DeleteRegular aria-hidden="true" />}
                                    aria-label={`Delete ${attachment.filename}`}
                                    className={fileCardStyles.fileDeleteButton}
                                    onClick={() =>
                                      void handleDeleteAttachment(attachment.id)
                                    }
                                  />
                                </Tooltip>
                              </>
                            )}
                          </div>
                        );
                      })}

                      {pendingAttachments.map((attachment) => {
                        const badge = getFileTypeBadgeLabel(
                          attachment.file.name,
                        );
                        const isXlsx = attachment.file.name
                          .toLowerCase()
                          .endsWith(".xlsx");

                        return (
                          <div
                            key={attachment.id}
                            className={styles.pendingCard}
                          >
                            <div className={styles.pendingCardHeader}>
                              <div className={styles.pendingCardFileInfo}>
                                <span
                                  className={mergeClasses(
                                    styles.fileTypeBadge,
                                    badge === "CSV"
                                      ? styles.fileTypeBadgeCsv
                                      : styles.fileTypeBadgeXls,
                                  )}
                                  aria-label={`File type: ${badge}`}
                                >
                                  {badge}
                                </span>
                                <DocumentRegular aria-hidden="true" />
                                <span className={styles.pendingFileName}>
                                  {attachment.file.name}
                                </span>
                                <span className={styles.pendingFileSize}>
                                  {formatFileSize(attachment.file.size)}
                                </span>
                              </div>
                              <div className={styles.pendingCardActions}>
                                {attachment.isUploading && (
                                  <Spinner
                                    size="tiny"
                                    aria-label="Uploading..."
                                  />
                                )}
                                <Tooltip
                                  content={
                                    attachment.isUploading ? "Cancel" : "Remove"
                                  }
                                  relationship="label"
                                >
                                  <Button
                                    appearance="subtle"
                                    size="small"
                                    icon={<DeleteRegular aria-hidden="true" />}
                                    aria-label={`Remove ${attachment.file.name}`}
                                    onClick={() =>
                                      handleRemovePending(attachment.id)
                                    }
                                    disabled={attachment.isUploading}
                                  />
                                </Tooltip>
                              </div>
                            </div>

                            <div className={styles.fieldsRow}>
                              {!isXlsx && (
                                <div>
                                  <Field label="Delimiter" size="small">
                                    <Select
                                      value={attachment.delimiter}
                                      onChange={(_, data) =>
                                        void handleDelimiterChange(
                                          attachment.id,
                                          data.value,
                                        )
                                      }
                                      disabled={attachment.isUploading}
                                      size="small"
                                      className={styles.fieldSelect}
                                      aria-label="Delimiter"
                                    >
                                      {DELIMITER_OPTIONS.map((opt) => (
                                        <option
                                          key={opt.value}
                                          value={opt.value}
                                        >
                                          {opt.label}
                                        </option>
                                      ))}
                                    </Select>
                                  </Field>
                                </div>
                              )}

                              {isXlsx && attachment.sheetNames.length > 0 && (
                                <div>
                                  <Field label="Sheet" size="small">
                                    <Select
                                      value={attachment.selectedSheet}
                                      onChange={(_, data) =>
                                        void handleSheetChange(
                                          attachment.id,
                                          data.value,
                                        )
                                      }
                                      disabled={attachment.isUploading}
                                      size="small"
                                      className={styles.fieldSelect}
                                      aria-label="Sheet name"
                                    >
                                      {attachment.sheetNames.map((sheet) => (
                                        <option key={sheet} value={sheet}>
                                          {sheet}
                                        </option>
                                      ))}
                                    </Select>
                                  </Field>
                                </div>
                              )}

                              <div>
                                <Field
                                  label="Content Column"
                                  size="small"
                                  required
                                  validationState={
                                    hasAttemptedSubmit &&
                                    !attachment.contentColumn
                                      ? "error"
                                      : "none"
                                  }
                                  validationMessage={
                                    hasAttemptedSubmit &&
                                    !attachment.contentColumn
                                      ? "Required"
                                      : undefined
                                  }
                                >
                                  <Select
                                    value={attachment.contentColumn}
                                    onChange={(_, data) =>
                                      void handleContentColumnChange(
                                        attachment.id,
                                        data.value,
                                      )
                                    }
                                    disabled={attachment.isUploading}
                                    size="small"
                                    className={styles.fieldSelectWide}
                                    aria-label="Content column"
                                  >
                                    <option value="">Select column...</option>
                                    {attachment.columns.map((col) => (
                                      <option key={col} value={col} title={col}>
                                        {col.length > 25
                                          ? `${col.substring(0, 25)}...`
                                          : col}
                                      </option>
                                    ))}
                                  </Select>
                                </Field>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {saveError && (
                <div role="alert" className={contentStyles.feedbackErrorBox}>
                  {saveError}
                </div>
              )}
            </div>
          </DialogContent>
        </DialogBody>

        {!isLoading && (
          <div className={footerStyles.dialogFooter}>
            {activeStep !== "basic-info" && (
              <DialogFooterAction
                icon={<ArrowLeftRegular />}
                tooltip={DIALOG_LABELS.BACK}
                onClick={handleBack}
              />
            )}
            {!isLastStep && (
              <DialogFooterAction
                icon={<ArrowRightRegular />}
                tooltip={DIALOG_LABELS.NEXT}
                dimmed={isNextDisabled}
                ariaLabel={
                  isNextDisabled
                    ? DIALOG_LABELS.NEXT_DISABLED
                    : DIALOG_LABELS.NEXT
                }
                onClick={handleNext}
              />
            )}
            {isLastStep && (
              <span ref={createButtonRef} tabIndex={-1}>
                <DialogFooterAction
                  icon={<CheckmarkRegular />}
                  tooltip={
                    mode === "edit"
                      ? DIALOG_LABELS.SUBMIT_LABEL_EDIT
                      : DIALOG_LABELS.SUBMIT_LABEL
                  }
                  dimmed={isSubmitDisabled}
                  disabled={isSubmitDisabled}
                  ariaLabel={
                    isSubmitDisabled
                      ? mode === "edit"
                        ? DIALOG_LABELS.SUBMIT_LABEL_EDIT_DISABLED
                        : DIALOG_LABELS.SUBMIT_LABEL_DISABLED
                      : mode === "edit"
                        ? DIALOG_LABELS.SUBMIT_LABEL_EDIT
                        : DIALOG_LABELS.SUBMIT_LABEL
                  }
                  onClick={handleSubmit}
                />
              </span>
            )}
          </div>
        )}
      </DialogSurface>
    </Dialog>
  );
};
